import { giphyDarkGrey, giphyLightestGrey } from '@giphy/js-brand'
import React, { useContext } from 'react'
import styled from 'styled-components'
import { BORDER_RADIUS_MEDIUM, DUPLICATE_UPLOAD_ID_EXTENSION } from '../../../constants'
import { CreationToolsContext } from '../../../manager'
import CreationToolGradient from '../../../types/gradient'
import CreationToolOutputType from '../../../types/output-type'
import CreationToolSourceEditor from '../../../types/source-editor'
import { DuplicateClipIcon, DuplicateGifIcon, TrimEditorIcon } from '../../icons'
import { useAvailableEditors } from '../hooks/use-available-editors'
import { useDuplicatableType } from '../hooks/use-duplicatable-type'
import { CreationToolUploadContext } from '../manager'
import { CreationToolUploadSource } from '../types'
import Item from './dropdown-item'

const Dropdown = styled.div`
    background: #000;
    border-radius: ${BORDER_RADIUS_MEDIUM}px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
    box-sizing: border-box;
    color: ${giphyLightestGrey};
    font-size: 15px;
    left: 50%;
    margin-left: -110px;
    padding: 20px;
    position: absolute;
    top: 100%;
    width: 220px;

    &:before {
        border-bottom: 6px solid #000;
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        content: '';
        height: 0;
        left: 50%;
        margin-left: -6px;
        position: absolute;
        top: -6px;
        width: 0;
    }
`

const Items = styled.div`
    svg {
        transition: opacity 0.15s ease-out;
    }

    &:hover svg {
        opacity: 0.5;
    }

    &:hover > div:hover svg {
        opacity: 1;
    }
`

const Divider = styled.div`
    background: ${giphyDarkGrey};
    border-radius: 1px;
    height: 2px;
    margin: 6px 0;
`

type Props = {
    className?: string
    source: CreationToolUploadSource
    setIsPreviewing?: (isPreviewing: boolean) => void
    onEditorClick: (editor?: CreationToolSourceEditor) => void
}

const CreationUploadItemEditMenuDropdown = ({ className, source, setIsPreviewing, onEditorClick }: Props) => {
    const { tool } = useContext(CreationToolsContext)
    const { uploadSources, addUploadSource } = useContext(CreationToolUploadContext)

    const editGradient = CreationToolSourceEditor.gradient(CreationToolSourceEditor.Edit)
    const editColors = CreationToolGradient.colors(editGradient)
    const availableEditors = useAvailableEditors(source, tool)
    const duplicatableType = useDuplicatableType(source, uploadSources)
    const duplicatableIconColors = CreationToolGradient.colors(CreationToolGradient.IndigoPurple)
    const isDuplicatableAsClip = duplicatableType === CreationToolOutputType.Video

    const onDuplicateClick = () => {
        if (!duplicatableType) return

        setIsPreviewing && setIsPreviewing(false)
        addUploadSource({
            ...source,
            id: `${source.id}${DUPLICATE_UPLOAD_ID_EXTENSION}`,
            outputType: duplicatableType,
            isDuplicate: true,
        })
    }

    return (
        <Dropdown className={className}>
            <Items>
                <>
                    {availableEditors.map(editor => {
                        const title = CreationToolSourceEditor.menuTitle(editor)
                        const gradient = CreationToolSourceEditor.gradient(editor)
                        const colors = CreationToolGradient.colors(gradient)
                        const Icon = CreationToolSourceEditor.icon(editor)
                        return (
                            <Item key={editor} label={title} onClick={() => onEditorClick(editor)}>
                                <Icon colors={colors} />
                            </Item>
                        )
                    })}
                    <Item label="Trim" onClick={() => onEditorClick()}>
                        <TrimEditorIcon colors={editColors} />
                    </Item>
                </>
                {duplicatableType && (
                    <>
                        <Divider />
                        <Item
                            label={`Duplicate as ${CreationToolOutputType.title(duplicatableType)}`}
                            onClick={onDuplicateClick}
                        >
                            {isDuplicatableAsClip ? (
                                <DuplicateClipIcon colors={duplicatableIconColors} />
                            ) : (
                                <DuplicateGifIcon colors={duplicatableIconColors} />
                            )}
                        </Item>
                    </>
                )}
            </Items>
        </Dropdown>
    )
}

export default CreationUploadItemEditMenuDropdown
